import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { LUX_PLUS_SCHEDULE } from '~/consts/schedule';

import DateTimeWidget from '../../../Common/Elements/DateTimeWidget';

interface Props {
  label: string;
  type: string;
  schedule: App.Schedule;
  isModified: boolean;
  onChange: (schedule: App.Schedule, type: string) => void;
  onDelete: (id: number) => void;
  disabled: boolean;
  isDeleting?: boolean;
  warning?: string;
}

const ScheduleTableRow = memo((props: Props) => {
  const {
    label,
    type,
    schedule,
    isModified,
    onChange,
    onDelete,
    disabled,
    isDeleting: isExternallyDeleting = false,
    warning,
  } = props;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isInternallyDeleting, setInternallyDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const isDeleting = isInternallyDeleting || isExternallyDeleting;
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Always-current ref: updated synchronously on every render so callbacks
  // never go stale without needing `schedule` in their dependency arrays.
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  const confirmDelete = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setInternallyDeleting(true);

    const snackbarKey = enqueueSnackbar(`Schedule for ${label} deleted`, {
      variant: 'info',
      action: (key) => (
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;
            closeSnackbar(key);
            setInternallyDeleting(false);
          }}
        >
          Undo
        </Button>
      ),
      autoHideDuration: 5000,
    });

    undoTimerRef.current = setTimeout(() => {
      closeSnackbar(snackbarKey);
      onDelete(scheduleRef.current.id);
    }, 5000);
  }, [label, enqueueSnackbar, closeSnackbar, onDelete]);

  // Stable callbacks — depend on onChange/type but NOT on the schedule object.
  // scheduleRef.current always has the latest schedule at call time.
  const onStartChange = useCallback(
    (start: string) => {
      onChange({ ...scheduleRef.current, start }, type);
    },
    [onChange, type],
  );

  const onEndChange = useCallback(
    (end: string) => {
      onChange({ ...scheduleRef.current, end }, type);
    },
    [onChange, type],
  );

  const toggleRestrictPurchasesFlag = useCallback(
    (event) => {
      onChange({ ...scheduleRef.current, restrict_purchase_to_lux_plus_members: event.target.checked }, type);
    },
    [onChange, type],
  );

  // Memoized date range — only re-renders when start/end/disabled change.
  // Clicking "Restrict all" only changes restrict_purchase_to_lux_plus_members,
  // so the expensive DateTimeWidgets are skipped entirely.
  const dateRangeCell = useMemo(
    () => (
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DateTimeWidget disabled={disabled} value={schedule.start} onChange={onStartChange} />
          <ArrowRightAltIcon />
          <DateTimeWidget disabled={disabled} value={schedule.end} onChange={onEndChange} />
        </Stack>
      </TableCell>
    ),
    [schedule.start, schedule.end, disabled, onStartChange, onEndChange],
  );

  return (
    <TableRow>
      <TableCell>
        {label}
        {warning && (
          <Typography variant="caption" color="warning.main" display="block" mt={0.5}>
            {warning}
          </Typography>
        )}
      </TableCell>
      {dateRangeCell}
      {type === LUX_PLUS_SCHEDULE && (
        <TableCell>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!schedule.restrict_purchase_to_lux_plus_members}
                onChange={toggleRestrictPurchasesFlag}
              />
            }
            label="Restrict purchase to LuxPlus+ members"
          />
        </TableCell>
      )}
      <TableCell>
        <PermissionedComponent>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setIsDeleteDialogOpen(true)}
            disabled={isDeleting || disabled}
            aria-label={`Delete schedule for ${label}`}
            startIcon={<DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </PermissionedComponent>
      </TableCell>
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Delete schedule for {label}?</DialogTitle>
        <DialogContent>
          <Typography>This schedule will be removed. You will have 5 seconds to undo.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To avoid accidental removals, delete schedules from the bottom and work up.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
});

ScheduleTableRow.displayName = 'ScheduleTableRow';

export default ScheduleTableRow;
